/**
 * Pestañas.
 *
 * DOS DECISIONES QUE NO SON DE ESTILO
 *
 * 1. **La pestaña activa vive en la URL** (`?tab=…`). Es lo que hace que se pueda enviar un
 *    enlace a «la financiación de este piso», que el botón de atrás del navegador vuelva a la
 *    pestaña anterior y que una recarga no devuelva al usuario al principio. Con `useState`
 *    nada de eso funciona, y es exactamente lo que la gente intenta hacer.
 *
 * 2. **Teclado completo**: flechas para moverse, Inicio y Fin para los extremos, y un único
 *    `tabIndex=0` en la pestaña activa (patrón de tabulación itinerante de WAI-ARIA). Sin eso,
 *    tabular por una barra de seis pestañas obliga a pulsar seis veces para llegar al contenido.
 *
 * El panel NO se desmonta al cambiar: cada pestaña conserva su estado (un filtro escrito, un
 * formulario a medias) mientras se está en la pantalla.
 */
import { useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './Tabs.module.css';

export interface TabDefinition {
  id: string;
  label: string;
  /** Contador discreto a la derecha de la etiqueta: habitaciones, documentos… */
  count?: number;
  render: () => ReactNode;
}

interface TabsProps {
  tabs: TabDefinition[];
  /** Nombre del parámetro de la URL. Distinto por pantalla si hubiera dos barras a la vez. */
  param?: string;
  ariaLabel: string;
  /**
   * Contenido fijo a la derecha de la barra, el mismo en todas las pestañas (la ocupación en la
   * ficha del inmueble). Va FUERA del `tablist`: un lector de pantalla no debe anunciarlo como
   * una pestaña más ni las flechas deben pasar por él.
   */
  aside?: ReactNode;
}

export function Tabs({ tabs, param = 'tab', ariaLabel, aside }: TabsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const listRef = useRef<HTMLDivElement>(null);

  const fallback = tabs[0]?.id ?? '';
  const requested = searchParams.get(param);
  // Una pestaña inventada en la URL no debe dejar la pantalla en blanco.
  const active = tabs.some((tab) => tab.id === requested) ? (requested as string) : fallback;
  const activeIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.id === active),
  );

  const select = useCallback(
    (id: string) => {
      const next = new URLSearchParams(searchParams);
      // La primera pestaña no ensucia la URL: `?tab=resumen` no aporta nada.
      if (id === fallback) next.delete(param);
      else next.set(param, id);
      // `replace`: cambiar de pestaña no debe llenar el historial de entradas intermedias.
      setSearchParams(next, { replace: true });
    },
    [fallback, param, searchParams, setSearchParams],
  );

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();

    let next = activeIndex;
    if (event.key === 'ArrowRight') next = (activeIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (activeIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;

    const target = tabs[next];
    if (!target) return;
    select(target.id);
    listRef.current?.querySelectorAll('button')[next]?.focus();
  }

  const activeTab = useMemo(() => tabs[activeIndex], [tabs, activeIndex]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.bar}>
      <div
        ref={listRef}
        className={styles.list}
        role="tablist"
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
      >
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={index === activeIndex}
            aria-controls={`panel-${tab.id}`}
            tabIndex={index === activeIndex ? 0 : -1}
            className={styles.tab}
            onClick={() => select(tab.id)}
          >
            {tab.label}
            {typeof tab.count === 'number' && <span className={styles.count}>{tab.count}</span>}
          </button>
        ))}
      </div>
      {aside && <div className={styles.aside}>{aside}</div>}
      </div>

      {activeTab && (
        <div
          className={styles.panel}
          role="tabpanel"
          id={`panel-${activeTab.id}`}
          aria-labelledby={`tab-${activeTab.id}`}
          /* La clave reinicia la animación de entrada en cada cambio, como hace el layout al
             navegar entre pantallas. */
          key={activeTab.id}
        >
          {activeTab.render()}
        </div>
      )}
    </div>
  );
}
